import React, { useState } from "react";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { logEvent } from "../utils/analytics";
import { colors, radius, shadows } from "../utils/designTokens";
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";

/**
 * Sección "Seguridad → Cambiar contraseña", dentro de la plataforma (logueado).
 * Antes solo existía "olvidé mi contraseña" (deslogueado). Flujo:
 * reautenticar con la contraseña actual → updatePassword. Maneja cuentas Google
 * (no muestra el form), reautenticación reciente, y errores en castellano llano.
 *
 * Accesibilidad (público 30–90): autocomplete correcto, permitir pegar, botón
 * mostrar/ocultar ≥44px con aria-pressed, requisitos visibles desde el inicio,
 * errores/éxito con aria-live y éxito persistente (no un toast que se va).
 *
 * Se puede apagar con REACT_APP_FF_CAMBIO_CLAVE="false" (default: encendido).
 */

const MIN_LEN = 8;

const strengthOf = (pw) => {
  if (!pw) return { pct: 0, labelKey: "", color: colors.gray300 };
  let score = 0;
  if (pw.length >= MIN_LEN) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { pct: 33, labelKey: "weak", color: colors.error };
  if (score <= 3) return { pct: 66, labelKey: "ok", color: colors.warning };
  return { pct: 100, labelKey: "good", color: colors.success };
};

function PasswordInput({ id, label, value, onChange, autoComplete, shown, onToggle }) {
  const { t } = useTranslation("password");
  return (
    <div style={S.field}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <div style={S.inputWrap}>
        <input
          id={id}
          type={shown ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          maxLength={128}
          style={S.input}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={shown}
          aria-label={shown ? t("hide") : t("show")}
          style={S.eyeBtn}
        >
          {shown ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
        </button>
      </div>
    </div>
  );
}

export default function CambiarPasswordCard() {
  const { t } = useTranslation("password");
  const user = auth.currentUser;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!user) return null;

  const providers = (user.providerData || []).map((p) => p.providerId);
  const isPasswordAccount = providers.includes("password");
  const isGoogle = providers.includes("google.com");

  // Cuenta Google (sin contraseña propia) → no mostramos el form.
  if (!isPasswordAccount && isGoogle) {
    return (
      <div style={S.card}>
        <div style={S.title}><FaLock size={13} style={{ marginRight: 8, color: colors.primary }} />{t("googleTitle")}</div>
        <p style={{ ...S.body, margin: 0 }}>
          {t("googleBody")}
        </p>
      </div>
    );
  }

  const st = strengthOf(next);

  const validate = () => {
    if (!current) return t("validation.currentRequired");
    if (next.length < MIN_LEN) return t("validation.minLen", { n: MIN_LEN });
    if (next !== confirm) return t("validation.mismatch");
    if (next === current) return t("validation.sameAsCurrent");
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, next);
      try {
        await addDoc(collection(db, "audit"), {
          action: "password_changed",
          email: user.email,
          uid: user.uid,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          createdAt: serverTimestamp(),
        });
      } catch (_) { /* la auditoría no bloquea */ }
      logEvent("password_changed", { surface: "perfil" });
      setSuccess(t("success"));
      setCurrent(""); setNext(""); setConfirm("");
      setShowCurrent(false); setShowNew(false);
    } catch (err) {
      const code = err?.code || "";
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setError(t("errors.wrongCurrent"));
      } else if (code === "auth/requires-recent-login") {
        setError(t("errors.recentLogin"));
      } else if (code === "auth/weak-password") {
        setError(t("errors.weak", { n: MIN_LEN }));
      } else if (code === "auth/too-many-requests") {
        setError(t("errors.tooMany"));
      } else {
        setError(t("errors.generic"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={S.card} noValidate>
      <div style={S.title}><FaLock size={13} style={{ marginRight: 8, color: colors.primary }} />{t("title")}</div>

      {/* usuario oculto para que los gestores de contraseña asocien bien */}
      <input type="text" name="username" autoComplete="username" value={user.email || ""} readOnly hidden aria-hidden="true" />

      <PasswordInput
        id="seg-actual" label={t("currentLabel")} value={current}
        onChange={setCurrent} autoComplete="current-password"
        shown={showCurrent} onToggle={() => setShowCurrent((s) => !s)}
      />

      <PasswordInput
        id="seg-nueva" label={t("newLabel")} value={next}
        onChange={setNext} autoComplete="new-password"
        shown={showNew} onToggle={() => setShowNew((s) => !s)}
      />
      <div style={{ marginTop: -6 }}>
        <div style={S.reqRow}>
          <span style={{ color: next.length >= MIN_LEN ? colors.success : colors.gray500 }}>
            {next.length >= MIN_LEN ? "✓" : "•"} {t("minChars", { n: MIN_LEN })}
          </span>
          {next && st.labelKey && <span style={{ color: st.color, fontWeight: 400 }}>{t(`strength.${st.labelKey}`)}</span>}
        </div>
        {next && (
          <div style={S.barTrack} aria-hidden="true">
            <div style={{ ...S.barFill, width: `${st.pct}%`, background: st.color }} />
          </div>
        )}
      </div>

      <PasswordInput
        id="seg-repetir" label={t("repeatLabel")} value={confirm}
        onChange={setConfirm} autoComplete="new-password"
        shown={showNew} onToggle={() => setShowNew((s) => !s)}
      />

      <div aria-live="polite">
        {error && <div style={S.error}>{error}</div>}
        {success && (
          <div style={S.success}>
            <FaCheckCircle size={16} style={{ flexShrink: 0 }} />
            <span>{success}</span>
          </div>
        )}
      </div>

      <button type="submit" disabled={saving} style={{ ...S.submit, opacity: saving ? 0.6 : 1 }}>
        {saving ? t("submitting") : t("submit")}
      </button>
    </form>
  );
}

const INPUT_H = 46;
const S = {
  card: {
    background: colors.white,
    borderRadius: radius.md,
    padding: 16,
    border: `1px solid ${colors.gray200}`,
    boxShadow: shadows.sm,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  title: { fontSize: 15, fontWeight: 400, color: colors.gray900, display: "flex", alignItems: "center" },
  body: { fontSize: 14, color: colors.gray700, lineHeight: 1.6 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13.5, color: colors.gray700, fontWeight: 400 },
  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  input: {
    width: "100%",
    boxSizing: "border-box",
    height: INPUT_H,
    padding: "0 48px 0 14px",
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray300}`,
    background: colors.white,
    color: colors.gray900,
    fontSize: 16, // ≥16px evita zoom automático en iOS + legible para mayores
    fontWeight: 400,
    fontFamily: "inherit",
    outline: "none",
  },
  eyeBtn: {
    position: "absolute",
    right: 4,
    height: 44,
    width: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    color: colors.gray500,
    cursor: "pointer",
    borderRadius: radius.sm,
  },
  reqRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, color: colors.gray500 },
  barTrack: { height: 5, background: colors.gray200, borderRadius: 999, marginTop: 6, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 999, transition: "width .2s ease" },
  error: {
    fontSize: 14,
    color: colors.errorDark || colors.error,
    background: colors.errorLight || "#fde8e8",
    border: `1px solid ${colors.error}`,
    borderRadius: radius.sm,
    padding: "10px 12px",
    lineHeight: 1.5,
  },
  success: {
    fontSize: 14,
    color: colors.successDark || "#166534",
    background: colors.successLight || "#dcfce7",
    border: `1px solid ${colors.success}`,
    borderRadius: radius.sm,
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    lineHeight: 1.5,
  },
  submit: {
    height: 48,
    background: colors.primary,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: 15,
    fontWeight: 400,
    fontFamily: "inherit",
    cursor: "pointer",
  },
};
